import { useEffect, useMemo, useState } from 'react';

import { listDrives } from '../../../services/drives.js';

/**
 * The drives an attachment can be picked from, loaded the first time the picker
 * opens (CT-5528). Nothing is fetched for a form nobody attaches a file to.
 * A failed load leaves the list empty and the picker shows its own empty
 * message — attaching is optional, so it never blocks saving the note.
 */
export function useDrives({ enabled = false } = {}) {
  const [drives, setDrives] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled || loaded || failed) return undefined;
    // A flag rather than a request counter: `loaded` flipping re-runs this
    // effect's cleanup, and a counter bumped there would discard the very
    // response that caused it.
    let cancelled = false;

    listDrives()
      .then((items) => {
        if (cancelled) return;
        setDrives(items);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, loaded, failed]);

  // Derived, not stored: loading is simply "asked for and not answered yet".
  const loading = enabled && !loaded && !failed;

  return useMemo(() => ({ drives, loading }), [drives, loading]);
}

export default useDrives;
